import "react-toastify/dist/ReactToastify.css";
import { IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AttributeBars from "../../Dashboard/Component/CompAttributes/CompAttributes";
import { transformDataForMultibarChart } from "../../../utils/helpers";
import { useContext } from "react";
import { DiscoveryContext, useIsSelectedSection } from "../discovery-context";
import { tab } from "../BrandScore/BrandScore";
import Checkbox from "@mui/material/Checkbox";
import useBaseResponsesModal from "../../../Component/BaseResponseComponent/useBaseResponsesModal";
import BaseResponsesModal from "../../../Component/BaseResponseComponent/BaseResponsesModal";
import { AuthContext } from "../../../context/auth-context";

export default function AttributesOverview({ category, isCategoryOverview = false }) {
    const discoveryCtx = useContext(DiscoveryContext);
    const discoveryType = discoveryCtx?.type || "brand"
    let selectedLLM = discoveryCtx.state.selectedLLM;
    const selectedPersona = discoveryCtx.state.selectedPersona;
    const competitors = discoveryCtx.competitors;
    const primaryMarket = discoveryCtx.markets[0];
    const selectedCategory = isCategoryOverview ? category : discoveryCtx.state.selectedCategory;
    let attributeRef = discoveryCtx.positive_negative_attributes?.[selectedLLM]?.[selectedCategory]?.[primaryMarket]?.[selectedPersona];
    const { user } = useContext(AuthContext);
    const iswebSearchLLM = selectedLLM?.toLowerCase()?.includes("search");
    const isCompanyUser = user?.user_role?.role?.name === "Company user";
    const rawBaseResponsesData = Array.isArray(attributeRef)
        ? attributeRef
            .filter(competitor => competitor?.baseResponses && Object.keys(competitor.baseResponses).length > 0)
            .map(competitor => ({
                brandName: competitor.name,
                baseResponses: competitor.baseResponses,
            }))
        : [];

    const orderedNames = [
        ...(competitors?.default ?? []),
        ...(competitors?.[selectedCategory] ?? []),
    ].map(c => (c.display_name || c.name || "").toLowerCase().trim());

    const dataMap = new Map(
        rawBaseResponsesData.map(item => [item.brandName.toLowerCase().trim(), item])
    );

    const allBaseResponsesData = orderedNames
        .map(name => dataMap.get(name))
        .filter(Boolean);

    const hasBaseResponses = allBaseResponsesData.length > 0;

    let positiveAttributes = {};
    let negativesAttributes = {};
    const id = "Attributes_Overview";
    let uniqueKey = `${tab.competitiveOverview}_${id}_${selectedLLM}_${selectedCategory}_${selectedPersona}`;
    const { checked } = useIsSelectedSection({ id, uniqueKey });

    const {
        infoModalOpen,
        infoModalTitle,
        infoModalContent,
        closeInfoModal,
        handleSignalStrengthClick,
    } = useBaseResponsesModal({}, "signal-strength");

    if (Array.isArray(attributeRef)) {
        if (attributeRef.length > 0) {
            attributeRef.forEach((competitor) => {
                if (competitor?.positives?.length > 0) {
                    positiveAttributes[competitor.name] = competitor.positives.reduce((a, b) => {
                        return { ...a, [b.name]: b.value };
                    }, {});
                }
                if (competitor?.negatives?.length > 0) {
                    negativesAttributes[competitor.name] = competitor.negatives.reduce((a, b) => {
                        return { ...a, [b.name]: b.value };
                    }, {});
                }
            });
        }
    } else if (attributeRef && typeof attributeRef === 'object') {
        if (attributeRef?.positives?.length > 0) {
            positiveAttributes[attributeRef.name] = attributeRef.positives.reduce((a, b) => {
                return { ...a, [b.name]: b.value };
            }, {});
        }
        if (attributeRef?.negatives?.length > 0) {
            negativesAttributes[attributeRef.name] = attributeRef.negatives.reduce((a, b) => {
                return { ...a, [b.name]: b.value };
            }, {});
        }
    }

    let positiveAttBarChartData = transformDataForMultibarChart(positiveAttributes, true);
    let negativeAttBarChartData = transformDataForMultibarChart(negativesAttributes, true);

    return (
        <>
            <div className="Heading-checkbox">
                {/* Only show heading+icon in competitive overview (not category tab) */}
                {!isCategoryOverview && (
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <h5 style={{ margin: 0 }}>Positive and Negative Attributes</h5>
                        {!isCompanyUser && hasBaseResponses && (
                            <Tooltip title="View Base Prompt" arrow placement="top">
                                <IconButton
                                    size="small"
                                    onClick={() => handleSignalStrengthClick(iswebSearchLLM, allBaseResponsesData,discoveryType)}
                                    sx={{
                                        padding: "2px",
                                        color: "rgba(0, 0, 0, 0.54)",
                                        "&:hover": {
                                            backgroundColor: "rgba(33, 150, 243, 0.08)",
                                            color: "#2196F3",
                                        },
                                    }}
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: "18px" }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                )}

                {discoveryCtx.state.showReportCheckbox && !isCategoryOverview && (
                    <Checkbox
                        checked={checked}
                        onChange={() => {
                            discoveryCtx?.clickComponentCheckbox({
                                uniqueKey,
                                filter: {
                                    id: id,
                                    title: "Positive and Negative Attributes",
                                    tab: tab.competitiveOverview,
                                    uniqueKey
                                },
                                checked,
                                id,
                            });
                        }}
                        inputProps={{ "aria-label": "controlled" }}
                    />
                )}
            </div>
            {!isCategoryOverview && <hr />}
            <AttributeBars
                negativeAttributes={negativeAttBarChartData}
                positiveAttributes={positiveAttBarChartData}
            />

            <BaseResponsesModal
                open={infoModalOpen}
                title={infoModalTitle}
                content={infoModalContent}
                onClose={closeInfoModal}
            />
        </>
    );
}